'use client';

import React, { useState, useMemo } from 'react';
import { Plus, Search, Eye, CheckCircle2, Clock, TrendingUp, X, Bird, Hash, ChevronDown, Info,  } from 'lucide-react';
import { Card } from '@/components/ui/Cards';
import {
  FARMS, HOUSES_BY_FARM, MOCK_FLOCK_SUMMARIES, MOVEMENT_STATUS_LABELS,
  type BirdMovementStatus,
} from '../../components/birdRecordsData';
import { useCreateMovement, useUpdateMovementStatus, useBirdMovements } from '@/hooks/useBirdMovements';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

interface PlacementFormData {
  placementDate: string;
  farm: string;
  house: string;
  flockId: string;
  flockNumber: string;
  flockName: string;
  birdType: string;
  breed: string;
  quantity: string;
  supplier: string;
  sourceLocation: string;
  ageAtPlacement: string;
  unitCost: string;
  currency: string;
  vehicleRef: string;
  receivedBy: string;
  notes: string;
}

const EMPTY_FORM: PlacementFormData = {
  placementDate: new Date().toISOString().split('T')[0],
  farm: '',
  house: '',
  flockId: '',
  flockNumber: '',
  flockName: '',
  birdType: '',
  breed: '',
  quantity: '',
  supplier: '',
  sourceLocation: '',
  ageAtPlacement: '1',
  unitCost: '',
  currency: 'UGX',
  vehicleRef: '',
  receivedBy: '',
  notes: '',
};

const BIRD_TYPES = ['Broiler', 'Layer', 'Breeder', 'Local/Indigenous', 'Chick', 'Pullet', 'Cockerel', 'Other'];
const BREEDS_BY_TYPE: Record<string, string[]> = {
  Broiler: ['Cobb 500', 'Ross 308', 'Arbor Acres', 'Hubbard Classic', 'Other'],
  Layer: ['Lohmann Brown', 'Hy-Line Brown', 'ISA Brown', 'Bovans Brown', 'Other'],
  Breeder: ['Ross Breeder', 'Cobb Breeder', 'Lohmann Breeder', 'Other'],
  'Local/Indigenous': ['Uganda Local', 'Kuroiler', 'KALRO Improved', 'Other'],
  Chick: ['Day-Old Broiler Chick', 'Day-Old Layer Chick', 'Day-Old Breeder Chick', 'Other'],
  Pullet: ['Lohmann Pullet', 'Hy-Line Pullet', 'Other'],
  Cockerel: ['Local Cockerel', 'Improved Cockerel', 'Other'],
  Other: ['Other'],
};
const CURRENCIES = ['UGX', 'USD', 'KES', 'TZS'];
const RECORDERS = ['James Okello', 'Moses Wafula', 'Sarah Namukasa', 'Peter Ssemakula', 'Grace Atim', 'Farm Manager'];

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: BirdMovementStatus }) {
  const map: Record<BirdMovementStatus, string> = {
    draft: 'bg-gray-100 text-gray-600',
    pending_approval: 'bg-amber-100 text-amber-700',
    approved: 'bg-blue-100 text-blue-700',
    posted: 'bg-green-100 text-green-700',
    reversed: 'bg-purple-100 text-purple-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${map[status]}`}>
      {MOVEMENT_STATUS_LABELS[status]}
    </span>
  );
}

// ─── Placement Form Modal ─────────────────────────────────────────────────────

function PlacementFormModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState<PlacementFormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof PlacementFormData, string>>>({});
  const [flockMode, setFlockMode] = useState<'existing' | 'new'>('existing');

  const createMovement = useCreateMovement();

  const availableHouses = form.farm ? (HOUSES_BY_FARM[form.farm] ?? []) : [];
  const availableFlocks = useMemo(() => {
    if (!form.farm) return MOCK_FLOCK_SUMMARIES;
    return MOCK_FLOCK_SUMMARIES.filter((f) => f.farm === form.farm);
  }, [form.farm]);
  const breeds = form.birdType ? (BREEDS_BY_TYPE[form.birdType] ?? ['Other']) : [];

  const set = (field: keyof PlacementFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleFlockSelect = (flockId: string) => {
    const flock = MOCK_FLOCK_SUMMARIES.find((f) => f.flockId === flockId);
    if (flock) {
      setForm((prev) => ({
        ...prev,
        flockId: flock.flockId,
        flockNumber: flock.flockNumber,
        flockName: flock.flockName,
        birdType: flock.birdType,
        breed: flock.breed,
        farm: flock.farm,
        house: flock.house,
      }));
    }
  };

  const validate = (): boolean => {
    const e: Partial<Record<keyof PlacementFormData, string>> = {};
    if (!form.placementDate) e.placementDate = 'Date is required';
    if (!form.farm) e.farm = 'Farm is required';
    if (!form.house) e.house = 'House is required';
    if (flockMode === 'existing' && !form.flockId) e.flockId = 'Select a flock';
    if (flockMode === 'new' && !form.flockNumber) e.flockNumber = 'Flock number is required';
    if (flockMode === 'new' && !form.flockName) e.flockName = 'Flock name is required';
    if (!form.birdType) e.birdType = 'Bird type is required';
    if (!form.breed) e.breed = 'Breed is required';
    if (!form.quantity || isNaN(Number(form.quantity)) || Number(form.quantity) <= 0) e.quantity = 'Enter a valid quantity';
    if (!form.receivedBy) e.receivedBy = 'Received by is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const qty = Number(form.quantity);
    const flockId = flockMode === 'existing' ? form.flockId : `flock-new-${Date.now()}`;
    const flockNumber = flockMode === 'existing'
      ? (MOCK_FLOCK_SUMMARIES.find((f) => f.flockId === form.flockId)?.flockNumber ?? form.flockNumber)
      : form.flockNumber;
    const flockName = flockMode === 'existing'
      ? (MOCK_FLOCK_SUMMARIES.find((f) => f.flockId === form.flockId)?.flockName ?? form.flockName)
      : form.flockName;

    try {
      await createMovement.mutateAsync({
        organizationId: 'org-001',
        movementType: 'initial_placement',
        movementDate: form.placementDate,
        farmId: form.farm.toLowerCase().replace(/\s+/g, '-'),
        farmName: form.farm,
        houseId: form.house.toLowerCase().replace(/\s+/g, '-'),
        houseName: form.house,
        flockId,
        flockNumber,
        flockName,
        birdType: form.birdType,
        breed: form.breed,
        quantity: qty,
        direction: 'in',
        populationBefore: 0,
        populationAfter: qty,
        referenceType: 'placement',
        reason: form.supplier ? `Received from ${form.supplier}` : 'Initial flock placement',
        notes: [
          form.supplier && `Supplier: ${form.supplier}`,
          form.sourceLocation && `Source: ${form.sourceLocation}`,
          form.vehicleRef && `Vehicle: ${form.vehicleRef}`,
          form.ageAtPlacement && `Age at placement: ${form.ageAtPlacement} day(s)`,
          form.unitCost && `Unit cost: ${form.currency} ${Number(form.unitCost).toLocaleString()}`,
          form.notes,
        ].filter(Boolean).join(' | '),
        status: 'draft',
        recordedBy: form.receivedBy,
      });
      toast.success('Placement recorded — +IN ledger entry created as Draft');
      onClose();
    } catch {
      toast.error('Failed to record placement. Please try again.');
    }
  };

  const qty = Number(form.quantity) || 0;
  const unitCost = Number(form.unitCost) || 0;
  const totalCost = qty * unitCost;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-card rounded-2xl border border-border/60 shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/60 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#2563eb18' }}>
              <Bird size={18} style={{ color: '#2563eb' }} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">New Bird Placement</h2>
              <p className="text-xs text-muted-foreground">Records initial flock population — auto-generates +IN ledger entry</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Info Banner */}
        <div className="mx-6 mt-4 flex-shrink-0 flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs" style={{ backgroundColor: '#2563eb0d', color: '#2563eb' }}>
          <Info size={14} className="flex-shrink-0 mt-0.5" />
          <span>Submitting this form creates a <strong>Draft +IN movement</strong> in the Bird Movement Ledger. Post it after verification to update the live population count.</span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-5">

          {/* Flock Mode Toggle */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-2">Flock Assignment</label>
            <div className="flex gap-2">
              {(['existing', 'new'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setFlockMode(mode)}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium border transition-colors ${
                    flockMode === mode
                      ? 'border-blue-500 bg-blue-50 text-blue-700' :'border-border bg-muted/30 text-muted-foreground hover:bg-muted/50'
                  }`}
                >
                  {mode === 'existing' ? 'Assign to Existing Flock' : 'Create New Flock Record'}
                </button>
              ))}
            </div>
          </div>

          {/* Placement Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                Placement Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.placementDate}
                onChange={(e) => set('placementDate', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
              {errors.placementDate && <p className="text-xs text-red-500 mt-1">{errors.placementDate}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Age at Placement (days)</label>
              <input
                type="number"
                min="0"
                value={form.ageAtPlacement}
                onChange={(e) => set('ageAtPlacement', e.target.value)}
                placeholder="e.g. 1"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </div>
          </div>

          {/* Farm & House */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                Farm <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={form.farm}
                  onChange={(e) => { set('farm', e.target.value); set('house', ''); set('flockId', ''); }}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 appearance-none pr-8"
                >
                  <option value="">Select farm…</option>
                  {FARMS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
              {errors.farm && <p className="text-xs text-red-500 mt-1">{errors.farm}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                House / Location <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={form.house}
                  onChange={(e) => set('house', e.target.value)}
                  disabled={!form.farm}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 appearance-none pr-8 disabled:opacity-50"
                >
                  <option value="">Select house…</option>
                  {availableHouses.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
              {errors.house && <p className="text-xs text-red-500 mt-1">{errors.house}</p>}
            </div>
          </div>

          {/* Flock Selection / Creation */}
          {flockMode === 'existing' ? (
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                Flock <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={form.flockId}
                  onChange={(e) => handleFlockSelect(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 appearance-none pr-8"
                >
                  <option value="">Select flock…</option>
                  {availableFlocks.map((f) => (
                    <option key={f.flockId} value={f.flockId}>
                      {f.flockNumber} — {f.flockName} ({f.farm})
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
              {errors.flockId && <p className="text-xs text-red-500 mt-1">{errors.flockId}</p>}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  Flock Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.flockNumber}
                  onChange={(e) => set('flockNumber', e.target.value)}
                  placeholder="e.g. BR-2026-007"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
                {errors.flockNumber && <p className="text-xs text-red-500 mt-1">{errors.flockNumber}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  Flock Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.flockName}
                  onChange={(e) => set('flockName', e.target.value)}
                  placeholder="e.g. Batch G1 — Cobb 500"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
                {errors.flockName && <p className="text-xs text-red-500 mt-1">{errors.flockName}</p>}
              </div>
            </div>
          )}

          {/* Bird Type & Breed */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                Bird Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={form.birdType}
                  onChange={(e) => { set('birdType', e.target.value); set('breed', ''); }}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 appearance-none pr-8"
                >
                  <option value="">Select type…</option>
                  {BIRD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
              {errors.birdType && <p className="text-xs text-red-500 mt-1">{errors.birdType}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                Breed <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={form.breed}
                  onChange={(e) => set('breed', e.target.value)}
                  disabled={!form.birdType}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 appearance-none pr-8 disabled:opacity-50"
                >
                  <option value="">Select breed…</option>
                  {breeds.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
              {errors.breed && <p className="text-xs text-red-500 mt-1">{errors.breed}</p>}
            </div>
          </div>

          {/* Quantity & Cost */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                Quantity (birds) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={form.quantity}
                onChange={(e) => set('quantity', e.target.value)}
                placeholder="e.g. 5000"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
              {errors.quantity && <p className="text-xs text-red-500 mt-1">{errors.quantity}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Unit Cost</label>
              <input
                type="number"
                min="0"
                value={form.unitCost}
                onChange={(e) => set('unitCost', e.target.value)}
                placeholder="e.g. 4500"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Currency</label>
              <div className="relative">
                <select
                  value={form.currency}
                  onChange={(e) => set('currency', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 appearance-none pr-8"
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Cost Preview */}
          {qty > 0 && unitCost > 0 && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs" style={{ backgroundColor: '#05966918', color: '#059669' }}>
              <TrendingUp size={13} />
              <span>Total placement cost: <strong>{form.currency} {totalCost.toLocaleString()}</strong> ({qty.toLocaleString()} birds × {form.currency} {unitCost.toLocaleString()})</span>
            </div>
          )}

          {/* Supplier & Source */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Supplier / Hatchery</label>
              <input
                type="text"
                value={form.supplier}
                onChange={(e) => set('supplier', e.target.value)}
                placeholder="e.g. Kenchic Hatchery"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Source Location</label>
              <input
                type="text"
                value={form.sourceLocation}
                onChange={(e) => set('sourceLocation', e.target.value)}
                placeholder="e.g. Kampala Hatchery"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </div>
          </div>

          {/* Vehicle & Received By */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Vehicle / Delivery Ref</label>
              <input
                type="text"
                value={form.vehicleRef}
                onChange={(e) => set('vehicleRef', e.target.value)}
                placeholder="e.g. UAX 123K"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                Received By <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={form.receivedBy}
                  onChange={(e) => set('receivedBy', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 appearance-none pr-8"
                >
                  <option value="">Select person…</option>
                  {RECORDERS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
              {errors.receivedBy && <p className="text-xs text-red-500 mt-1">{errors.receivedBy}</p>}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={2}
              placeholder="Additional observations, health status on arrival, etc."
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border/60 bg-muted/20 flex-shrink-0">
          <p className="text-xs text-muted-foreground">
            Ledger entry will be created as <strong>Draft</strong> — post after verification
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-border hover:bg-muted/50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="placement-form"
              onClick={handleSubmit as unknown as React.MouseEventHandler}
              disabled={createMovement.isPending}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-60"
              style={{ backgroundColor: '#2563eb' }}
            >
              {createMovement.isPending ? 'Saving…' : 'Record Placement & Generate +IN Entry'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────

function PlacementDetailDrawer({ movement, onClose, onPost }: {
  movement: { id: string; movementNumber: string; movementDate: string; farmName: string; houseName?: string; flockNumber: string; flockName: string; birdType: string; breed: string; quantity: number; populationAfter?: number; notes?: string; status: BirdMovementStatus; recordedBy: string; approvedBy?: string };
  onClose: () => void;
  onPost: (id: string) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-card rounded-2xl border border-border/60 shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border/60">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{movement.movementNumber}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{movement.movementDate} · {movement.farmName}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={movement.status} />
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Flock', value: `${movement.flockNumber} — ${movement.flockName}` },
              { label: 'House', value: movement.houseName ?? '—' },
              { label: 'Bird Type', value: movement.birdType },
              { label: 'Breed', value: movement.breed },
              { label: 'Birds Placed (+IN)', value: movement.quantity.toLocaleString() },
              { label: 'Population After', value: (movement.populationAfter ?? movement.quantity).toLocaleString() },
              { label: 'Recorded By', value: movement.recordedBy },
              { label: 'Approved By', value: movement.approvedBy ?? '—' },
            ].map(({ label, value }) => (
              <div key={label} className="bg-muted/30 rounded-lg p-2.5">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-sm font-medium text-foreground mt-0.5 truncate">{value}</p>
              </div>
            ))}
          </div>
          {movement.notes && (
            <div className="bg-muted/30 rounded-lg p-2.5">
              <p className="text-xs text-muted-foreground mb-1">Notes</p>
              <p className="text-xs text-foreground">{movement.notes}</p>
            </div>
          )}
        </div>
        {(movement.status === 'draft' || movement.status === 'approved') && (
          <div className="px-4 pb-4 flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-border hover:bg-muted/50 transition-colors">Close</button>
            <button
              onClick={() => { onPost(movement.id); onClose(); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors"
              style={{ backgroundColor: '#059669' }}
            >
              Post to Ledger
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BirdPlacementsContent() {
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<BirdMovementStatus | 'all'>('all');
  const [selectedMovement, setSelectedMovement] = useState<string | null>(null);

  const { data: movements = [], isLoading } = useBirdMovements({ type: 'initial_placement' });
  const updateStatus = useUpdateMovementStatus();

  const filtered = useMemo(() => {
    let result = movements;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (m) =>
          m.movementNumber.toLowerCase().includes(q) ||
          m.flockNumber.toLowerCase().includes(q) ||
          m.flockName.toLowerCase().includes(q) ||
          m.farmName.toLowerCase().includes(q),
      );
    }
    if (statusFilter !== 'all') result = result.filter((m) => m.status === statusFilter);
    return result;
  }, [movements, search, statusFilter]);

  const stats = useMemo(() => {
    const total = movements.length;
    const posted = movements.filter((m) => m.status === 'posted').length;
    const draft = movements.filter((m) => m.status === 'draft').length;
    const totalBirds = movements.filter((m) => m.status === 'posted').reduce((s, m) => s + m.quantity, 0);
    return { total, posted, draft, totalBirds };
  }, [movements]);

  const handlePost = async (id: string) => {
    try {
      await updateStatus.mutateAsync({ id, status: 'posted', updatedBy: 'Farm Manager' });
      toast.success('Placement posted — population ledger updated');
    } catch {
      toast.error('Failed to post placement');
    }
  };

  const detailMovement = selectedMovement ? movements.find((m) => m.id === selectedMovement) : null;

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">Bird Placements</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Record initial flock population — auto-generates +IN ledger entries</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white transition-colors"
          style={{ backgroundColor: '#2563eb' }}
        >
          <Plus size={15} />
          New Placement
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Placements', value: stats.total, icon: Hash, color: '#2563eb' },
          { label: 'Posted to Ledger', value: stats.posted, icon: CheckCircle2, color: '#059669' },
          { label: 'Draft / Pending', value: stats.draft, icon: Clock, color: '#d97706' },
          { label: 'Total Birds Placed', value: stats.totalBirds.toLocaleString(), icon: Bird, color: '#7c3aed' },
        ].map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
                <Icon size={16} style={{ color }} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">{label}</p>
                <p className="text-lg font-bold text-foreground">{value}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by movement #, flock, farm…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as BirdMovementStatus | 'all')}
            className="pl-3 pr-8 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 appearance-none"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="posted">Posted</option>
            <option value="rejected">Rejected</option>
          </select>
          <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/30">
                {['Movement #', 'Date', 'Farm / House', 'Flock', 'Bird Type', 'Qty Placed', 'Ledger Entry', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="text-left text-xs font-semibold text-muted-foreground px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-muted-foreground text-sm">Loading placements…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <Bird size={32} className="text-muted-foreground/40" />
                      <p className="text-sm text-muted-foreground">No placement records found</p>
                      <button
                        onClick={() => setShowForm(true)}
                        className="mt-1 text-xs font-medium px-3 py-1.5 rounded-lg text-white"
                        style={{ backgroundColor: '#2563eb' }}
                      >
                        Record First Placement
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((m) => (
                  <tr key={m.id} className="border-b border-border/40 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono font-medium text-blue-600">{m.movementNumber}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{m.movementDate}</td>
                    <td className="px-4 py-3">
                      <p className="text-xs font-medium text-foreground">{m.farmName}</p>
                      <p className="text-xs text-muted-foreground">{m.houseName ?? '—'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs font-medium text-foreground">{m.flockNumber}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-[140px]">{m.flockName}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-foreground">{m.birdType}</p>
                      <p className="text-xs text-muted-foreground">{m.breed}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
                        <TrendingUp size={10} />
                        +{m.quantity.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#2563eb18', color: '#2563eb' }}>
                        +IN
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={m.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setSelectedMovement(m.id)}
                          className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors"
                          title="View details"
                        >
                          <Eye size={13} />
                        </button>
                        {(m.status === 'draft' || m.status === 'approved') && (
                          <button
                            onClick={() => handlePost(m.id)}
                            disabled={updateStatus.isPending}
                            className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 transition-colors disabled:opacity-50"
                            title="Post to ledger"
                          >
                            <CheckCircle2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-border/40 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Showing {filtered.length} of {movements.length} placement records</p>
            <p className="text-xs text-muted-foreground">
              {movements.filter((m) => m.status === 'posted').reduce((s, m) => s + m.quantity, 0).toLocaleString()} birds posted to ledger
            </p>
          </div>
        )}
      </Card>

      {/* Modals */}
      {showForm && <PlacementFormModal onClose={() => setShowForm(false)} />}
      {detailMovement && (
        <PlacementDetailDrawer
          movement={detailMovement}
          onClose={() => setSelectedMovement(null)}
          onPost={handlePost}
        />
      )}
    </div>
  );
}
